// ─────────────────────────────────────────────────────────────
// MeetTheTeamDialog — full-screen modal showcasing the six
// AI agents on the Maps Discover Studio team.
// ─────────────────────────────────────────────────────────────

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Avatar,
  Typography,
  Grid,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface Agent {
  avatar: string;
  name: string;
  role: string;
  description: string;
  color: string;
}

// Static assets in `public/` are served under Vite's `base` (e.g. "/" locally,
// "/discoverstudio/" in production). Using `import.meta.env.BASE_URL` keeps the
// avatar paths correct in both environments — `BASE_URL` always ends with "/".
const ASSET_BASE = import.meta.env.BASE_URL;

const AGENTS: Agent[] = [
  {
    avatar: `${ASSET_BASE}avatars/paul.png`,
    name: "Paul",
    role: "Project Manager",
    description:
      "Coordinates the team, manages sprint execution, and keeps everything on track. The central intelligence of the project.",
    color: "#6366f1",
  },
  {
    avatar: `${ASSET_BASE}avatars/jamie.png`,
    name: "Jamie",
    role: "Product Owner",
    description:
      "Manages the backlog, plans sprints, and fields feature requests. Your guide to shaping the product's direction.",
    color: "#ec4899",
  },
  {
    avatar: `${ASSET_BASE}avatars/sam.png`,
    name: "Sam",
    role: "Backend Engineer",
    description:
      "Owns the FastAPI backend, database, API endpoints, AI integrations, and script execution logic.",
    color: "#3b82f6",
  },
  {
    avatar: `${ASSET_BASE}avatars/mia.png`,
    name: "Mia",
    role: "Frontend Engineer",
    description:
      "Builds everything you see — the dashboard, the code editor, and all UI components. Material Design 3 is her language.",
    color: "#8b5cf6",
  },
  {
    avatar: `${ASSET_BASE}avatars/david.png`,
    name: "David",
    role: "Infrastructure & DevOps",
    description:
      "Keeps the containers running, manages deployments, and owns the sandboxed execution environment.",
    color: "#10b981",
  },
  {
    avatar: `${ASSET_BASE}avatars/todd.png`,
    name: "Todd",
    role: "QA Engineer",
    description:
      "Reviews every feature before it ships. Writes the tests, enforces the Definition of Done, and has veto power over anything insecure.",
    color: "#f59e0b",
  },
];

interface MeetTheTeamDialogProps {
  open: boolean;
  onClose: () => void;
}

export function MeetTheTeamDialog({ open, onClose }: MeetTheTeamDialogProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Slightly lighter card surface relative to the dialog background
  const cardBg = isDark
    ? "rgba(255, 255, 255, 0.05)"
    : "rgba(0, 0, 0, 0.03)";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          // Ensure dialog has a solid background so cards read against it
          bgcolor: "background.paper",
        },
      }}
    >
      {/* ── Header ──────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Meet the Team
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.25 }}
          >
            Six AI specialists, one shared goal.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close dialog"
          sx={{ ml: 2 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* ── Agent grid ──────────────────────────────────────── */}
      <DialogContent sx={{ pt: 3, pb: 4 }}>
        <Grid container spacing={3}>
          {AGENTS.map((agent) => (
            <Grid
              key={agent.name}
              item
              xs={12}
              sm={6}
              md={4}
            >
              <Box
                sx={{
                  bgcolor: cardBg,
                  borderRadius: 2,
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  height: "100%",
                  transition: "background-color 0.15s ease",
                  "&:hover": {
                    bgcolor: isDark
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.06)",
                  },
                }}
              >
                {/* Avatar */}
                <Avatar
                  src={agent.avatar}
                  alt={agent.name}
                  sx={{
                    width: 104,
                    height: 104,
                    mb: 2,
                    border: `3px solid ${agent.color}`,
                  }}
                />

                {/* Name */}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: agent.color,
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {agent.name}
                </Typography>

                {/* Role */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 500,
                    mb: 1.5,
                    display: "block",
                  }}
                >
                  {agent.role}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  {agent.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
